import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { HighWindDestinationShapeId, HighWindShapeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import { MaterialId } from '@core/constants/Material';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
} from '../../interfaces/Form';
import additionalDataFactory from './common/fieldsAdditionalData';
import fieldsContact from './common/fieldsContact';
import {
    allGlasses, allMetals, allPlastics, allSolidWoods, getMaterialsWithExceptions,
} from './common/materialGroups';

const HighWindForm: IForm = {
    formId       : PublishedFormId.HighWind,
    type         : FormTypeId.GlassAndMirror,
    thumbnailURL : '/resized-images/forms/highWind.jpg',
    fieldsOrder  : [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.HighWindDestination,
        IFormFieldKey.ShapeDimension,
        IFormFieldKey.Material,
        IFormFieldKey.FrameMaterial,
        IFormFieldKey.HighWindLaying,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        highWindDestination: {
            value   : undefined,
            options : [
                HighWindDestinationShapeId.HighWindWindow,
                HighWindDestinationShapeId.HighWindDoor,
            ],
            required: true,
        },
        shapeDimension: {
            shape: {
                value   : undefined,
                options : [
                    HighWindShapeId.SimpleHighWind,
                    HighWindShapeId.RoundedHighWind,
                    HighWindShapeId.HalfCircleHighWind,
                ],
                required: true,
            },
            dimension: {
                options: {
                    [HighWindShapeId.SimpleHighWind]: {
                        [FieldDimensionId.Length]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 350, max: 2000 },
                            required     : true,
                            defaultValue : 1000,
                        },
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 200, max: 1500 },
                            required     : true,
                            defaultValue : 1000,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 2,
                            range        : { min: 8, max: 20 },
                            required     : true,
                            defaultValue : 10,
                        },
                    },
                    [HighWindShapeId.RoundedHighWind]: {
                        [FieldDimensionId.Length]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 350, max: 2000 },
                            required     : true,
                            defaultValue : 1000,
                        },
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 200, max: 1500 },
                            required     : true,
                            defaultValue : 1000,
                        },
                        [FieldDimensionId.Radius]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 100, max: 1000 },
                            required     : true,
                            defaultValue : 200,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 2,
                            range        : { min: 8, max: 20 },
                            required     : true,
                            defaultValue : 10,
                        },
                    },
                    [HighWindShapeId.HalfCircleHighWind]: {
                        [FieldDimensionId.Length]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 350, max: 2000 },
                            required     : true,
                            defaultValue : 1000,
                        },
                        [FieldDimensionId.Radius]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 200, max: 1500 },
                            required     : true,
                            defaultValue : 500,
                        },
                        [FieldDimensionId.Thickness]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 2,
                            range        : { min: 8, max: 20 },
                            required     : true,
                            defaultValue : 10,
                        },
                    },
                },
            },
        },
        material: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allGlasses,
                        ...allPlastics,
                        ...allMetals,
                    ],
                    [
                        MaterialId.Pvc,
                        MaterialId.PvcRigid,
                        MaterialId.RecycledPlastic,
                        MaterialId.PlexiglassFluorescent,
                        MaterialId.PlexiglassFrosted,
                        MaterialId.PlexiglassMirror,
                        MaterialId.PlexiglassTransparent,
                        MaterialId.PlexiglassWhite,
                        MaterialId.Rust,
                        MaterialId.Brass,
                        MaterialId.ThermoLaqueredBlack,
                        MaterialId.ThermoLaqueredWhite,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                    ],
                ),
                required: true,
            },
        },
        frameMaterial: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allSolidWoods,
                        ...allMetals,
                    ],
                    [
                        MaterialId.BlackLocust,
                        MaterialId.Iroko,
                        MaterialId.Larch,
                        MaterialId.Padouk,
                        MaterialId.Ipe,
                        MaterialId.DriftWood,
                        MaterialId.WagonfloorDark,
                        MaterialId.WagonfloorLight,
                        MaterialId.OakOld,
                        MaterialId.Walnut,
                        MaterialId.AshOlive,
                        MaterialId.Acacia,
                        MaterialId.Mahogany,
                        MaterialId.Elm,
                        MaterialId.Birch,
                        MaterialId.Maple,
                        MaterialId.Teak,
                        MaterialId.Wenge,
                        MaterialId.Cherrywood,
                        MaterialId.Ebony,
                        MaterialId.Rubber,
                        MaterialId.Garapa,
                        MaterialId.Rust,
                        MaterialId.Brass,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                    ],
                ),
                required: true,
            },
        },
        highWindLaying: {
            value: undefined,
        },
        additionalData: additionalDataFactory({
            budgetMin          : 1500,
            budgetMax          : 7000,
            budgetDefaultValue : 3500,
        }),
        contact: fieldsContact,
    },
};

export default HighWindForm;
